import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Game, Team } from '../../types';
import { useAuth } from '../../auth/AuthProvider';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Text } from '../../ui/text';
import { EmptyState, PageHeader, Skeleton, StatCard } from '../../components/PageChrome';

export default function AdminHome() {
  const { email, groups } = useAuth();
  const [teams, setTeams] = useState<Team[]>();
  const [games, setGames] = useState<Game[]>([]);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await admin.listTeams();
        const gameResults = await Promise.allSettled(
          res.teams.map((team) => admin.listGames(team.teamId)),
        );
        if (!cancelled) {
          setTeams(res.teams);
          setGames(
            gameResults.flatMap((result) =>
              result.status === 'fulfilled' ? result.value.games : [],
            ),
          );
        }
      } catch (err) {
        if (!cancelled) setError(String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadingTeams = teams === undefined;
  const teamList = teams ?? [];
  const activeTeams = teamList.filter((t) => t.active).length;
  const channelReady = teamList.filter((t) => t.channelArn).length;
  const liveGames = games.filter((g) => g.status === 'LIVE').length;
  const upcomingGames = games.filter((g) => g.status === 'SCHEDULED').length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Game-day control room"
        description={
          <>
            Signed in as <strong className="text-zinc-100">{email}</strong>
            {groups.length ? ` (${groups.join(', ')}).` : '.'}
          </>
        }
        actions={<Button href="/admin/teams" color="dark/zinc">Manage teams</Button>}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Teams"
          value={loadingTeams ? '—' : activeTeams}
          hint={loadingTeams ? ' ' : `${teamList.length} total`}
        />
        <StatCard
          label="Camera setup"
          value={loadingTeams ? '—' : channelReady}
          hint="Teams ready to go live"
          tone={!loadingTeams && channelReady === teamList.length && teamList.length > 0 ? 'emerald' : 'zinc'}
        />
        <StatCard
          label="Live now"
          value={loadingTeams ? '—' : liveGames}
          tone={liveGames > 0 ? 'red' : 'zinc'}
        />
        <StatCard
          label="Upcoming"
          value={loadingTeams ? '—' : upcomingGames}
          hint="Scheduled games"
        />
      </div>

      {error ? <Text className="text-red-400">Error: {error}</Text> : null}

      {loadingTeams ? (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-zinc-100">Teams</div>
          </div>
          <ul className="grid gap-3 lg:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <li key={i}>
                <Skeleton className="h-24 w-full" />
              </li>
            ))}
          </ul>
        </section>
      ) : teamList.length === 0 ? (
        <EmptyState
          title="No teams yet"
          body="Create a team before scheduling games, building a roster, or going live."
          action={<Button href="/admin/teams" color="dark/zinc">Create team</Button>}
        />
      ) : (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-zinc-100">Teams</div>
            <Button href="/admin/teams" outline>View all</Button>
          </div>
          <ul className="grid gap-3 lg:grid-cols-2">
            {teamList.map((t) => (
              <li key={t.teamId}>
                <Link
                  to={`/admin/teams/${t.teamId}`}
                  className="block rounded-lg border border-zinc-800 bg-zinc-900 p-4 transition hover:border-zinc-700"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-100">{t.name}</div>
                      <Text className="mt-1 font-mono text-xs text-zinc-500">{t.teamId}</Text>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <Badge color={t.active ? 'lime' : 'zinc'}>
                        {t.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <Text className="mt-3 text-sm">{t.season ?? 'No season set'}</Text>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
